import React, { useEffect, useState } from 'react';
import { HomeSection } from '@fullstory/proto/fs/services/dx-objects';
import { getHome, HomeRequestActions, fullstorySelectors, AppDispatch } from '@fullstory/redux';
import { useSelector, useDispatch } from 'react-redux';
import {
  Spinner,
  Select,
  List,
  MenuItem,
  ListItemText,
  Box,
  Flex,
  Tooltip,
} from '@fullstory/components';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import { generateRoutePath, HOME_ROUTE } from '@fullstory/routes';
import { trackUserBaseSelector, trackHomeCardAdded, SpecAnalytics } from '@fullstory/analytics';
import { useIntl } from 'react-intl';
import { intl } from '@fullstory/intl';
import { generateScrollToURL } from '../../hooks';
import { HomeCardWithoutId, LibraryCardType } from '../../types';
import { toHomeCardAnalytics } from '../../utils/cardsAnalytics';

export const testIds = { ADD_TO_HOME_DROPDOWN_SELECT: 'add-to-home-dropdown-select' };

const { getOrgId } = fullstorySelectors;

const addToHomeMessages: { [key in LibraryCardType]: { tooltipContent: string } } = {
  metric: {
    tooltipContent: intl.formatMessage({
      defaultMessage: 'Metric must be saved if you want to add to Home',
      id: '62b6m1',
    }),
  },
  funnel: {
    tooltipContent: intl.formatMessage({
      defaultMessage: 'Funnel must be saved if you want to add to Home',
      id: '4WbWmd',
    }),
  },
  heatmap: {
    tooltipContent: intl.formatMessage({
      defaultMessage: 'Heatmap must be saved if you want to add to Home',
      id: 'Zoc7ae',
    }),
  },
  retention: {
    tooltipContent: intl.formatMessage({
      defaultMessage: 'Retention must be saved if you want to add to Home',
      id: '0l3G03',
    }),
  },
  segment: {
    tooltipContent: intl.formatMessage({
      defaultMessage: 'Segment must be saved if you want to add to Home',
      id: 'bshT9D',
    }),
  },
  dashboard: {
    tooltipContent: intl.formatMessage({
      defaultMessage: 'Dashboard must be saved if you want to add to Home',
      id: '8mS3Eg',
    }),
  },
};

export const AddToHomeDropdown: React.FC<{
  homeCard: HomeCardWithoutId;
  validate: () => boolean;
  disabled: boolean;
  objectType: LibraryCardType;
  specAnalytics: SpecAnalytics;
}> = ({ homeCard, validate, disabled, objectType, specAnalytics }) => {
  const flags = useFlags();
  const intl = useIntl();
  const orgId = useSelector(getOrgId);
  const userAnalytics = useSelector(trackUserBaseSelector);

  const dispatch = useDispatch<AppDispatch>();
  useEffect(() => {
    dispatch(HomeRequestActions.getHome({ orgId }, flags));
  }, [dispatch, flags, orgId]);

  const { data, loading } = useSelector(getHome);
  const [open, setOpen] = useState(false);
  const [highlightedSectionId, setHighlightedSectionId] = useState<string | null>(null);
  const notifications = useNotifications();

  const onClick = async (section: HomeSection) => {
    const isValid = validate();
    if (isValid) {
      try {
        const cards = section.cards;

        const updatedHome = await dispatch(
          HomeRequestActions.setHomeSection(
            {
              orgId,
              section: {
                ...section,
                cards: [...cards, { ...homeCard, id: '' }],
              },
            },
            flags,
          ),
        );

        const updatedSection = updatedHome.sections.find(s => s.id === section.id);
        const newCardId = updatedSection?.cards[updatedSection.cards.length - 1]?.id;

        const homeUrl = generateRoutePath(HOME_ROUTE, {
          orgId,
        });

        trackHomeCardAdded({
          ...userAnalytics,
          ...specAnalytics,
          ...toHomeCardAnalytics({ id: newCardId ?? '', ...homeCard }, section.id),
        });

        notifications.successLink(
          intl.formatMessage({
            defaultMessage: 'This card has been added to your Home. ',
            id: '8IX8is',
          }),
          intl.formatMessage({ defaultMessage: 'See it now.', id: 'gzvnO9' }),
          generateScrollToURL(homeUrl, newCardId),
        );
      } catch (e) {
        notifications.error(
          intl.formatMessage({ defaultMessage: 'Failed to add to your Home.', id: 'SRpNZq' }),
        );
      }
    }
    setOpen(false);
  };

  const selectProps = {
    open,
    displayName: intl.formatMessage({ defaultMessage: 'Add to Home', id: '38oPbc' }),
    variant: 'outline' as 'outline',
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
  };

  const messages = addToHomeMessages[objectType];

  return (
    <Tooltip
      data-component="AddToHomeDropdown"
      testId="add-to-home-button-tooltip"
      disabled={!disabled}
      tooltipContent={messages.tooltipContent}
    >
      {loading ? (
        <Select disabled={disabled} {...selectProps} testId={testIds.ADD_TO_HOME_DROPDOWN_SELECT}>
          <Box minWidth={160} padding={8}>
            <Flex>
              <Spinner />
            </Flex>
          </Box>
        </Select>
      ) : (
        <Select disabled={disabled} {...selectProps} testId={testIds.ADD_TO_HOME_DROPDOWN_SELECT}>
          <List>
            {data?.sections.length ? (
              data?.sections.map(section => (
                <MenuItem
                  onClick={() => onClick(section)}
                  onMouseLeave={() => setHighlightedSectionId(null)}
                  onMouseEnter={() => setHighlightedSectionId(section.id)}
                  highlighted={highlightedSectionId === section.id}
                  key={section.id}
                >
                  <ListItemText text={section.name} />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <ListItemText
                  text={intl.formatMessage({ defaultMessage: 'No Home sections.', id: 'Rtn1Xc' })}
                />
              </MenuItem>
            )}
          </List>
        </Select>
      )}
    </Tooltip>
  );
};
